/**
 * Pachisi AI
 *
 * Three difficulty levels:
 * - Easy: random valid move, 500ms delay
 * - Medium: priority-based (capture > safe square > enter > advance closest), 1s delay
 * - Hard: scoring function with blocking and risk assessment, 2s delay
 */

import {
  PachisiDifficulty,
  PachisiMove,
  PlayerColor,
  SAFE_SQUARES,
  PLAYER_START_POSITIONS,
  HOME_COLUMN_ENTRY,
  TRACK_LENGTH,
  HOME_COLUMN_LENGTH,
} from './types';
import { PachisiEngine } from './PachisiEngine';

export class PachisiAI {
  private difficulty: PachisiDifficulty;
  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(difficulty: PachisiDifficulty = 'medium') {
    this.difficulty = difficulty;
  }

  /** Select the best move for the given difficulty */
  selectMove(
    engine: PachisiEngine,
    player: PlayerColor,
    moveValue: number,
    validMoves: PachisiMove[]
  ): PachisiMove {
    if (validMoves.length === 0) {
      return { player, pawnIndex: 0, action: 'skip', moveValue };
    }
    if (validMoves.length === 1) {
      return validMoves[0];
    }

    switch (this.difficulty) {
      case 'easy':
        return this.selectEasy(validMoves);
      case 'medium':
        return this.selectMedium(engine, player, validMoves);
      case 'hard':
        return this.selectHard(engine, player, validMoves);
      default:
        return this.selectEasy(validMoves);
    }
  }

  /** Get the delay in ms before the AI acts */
  getDelay(): number {
    switch (this.difficulty) {
      case 'easy': return 500;
      case 'medium': return 1000;
      case 'hard': return 2000;
      default: return 1000;
    }
  }

  setDifficulty(difficulty: PachisiDifficulty): void {
    this.difficulty = difficulty;
  }

  getDifficulty(): PachisiDifficulty {
    return this.difficulty;
  }

  /** Schedule an AI action after the configured delay */
  scheduleAction(onAction: () => void): () => void {
    this.cancelPending();
    const delay = this.getDelay();
    this.pendingTimeout = setTimeout(() => {
      this.pendingTimeout = null;
      onAction();
    }, delay);
    return () => this.cancelPending();
  }

  cancelPending(): void {
    if (this.pendingTimeout !== null) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
  }

  reset(): void {
    this.cancelPending();
  }

  destroy(): void {
    this.cancelPending();
  }

  // =========================================================================
  // Easy: Random
  // =========================================================================

  private selectEasy(validMoves: PachisiMove[]): PachisiMove {
    return validMoves[Math.floor(Math.random() * validMoves.length)];
  }

  // =========================================================================
  // Medium: Priority-based
  // =========================================================================

  private selectMedium(engine: PachisiEngine, player: PlayerColor, validMoves: PachisiMove[]): PachisiMove {
    // Priority 1: Capture
    for (const move of validMoves) {
      if (this.wouldCapture(engine, player, move)) {
        return move;
      }
    }

    // Priority 2: Move to safe square
    for (const move of validMoves) {
      if (this.wouldLandOnSafeSquare(engine, player, move)) {
        return move;
      }
    }

    // Priority 3: Enter new pawn (if fewer than 2 on board)
    if (engine.getPawnsOnBoard(player) < 2) {
      const enterMoves = validMoves.filter((m) => m.action === 'enter');
      if (enterMoves.length > 0) {
        return enterMoves[0];
      }
    }

    // Priority 4: Advance closest to home
    return this.selectClosestToHome(engine, player, validMoves);
  }

  // =========================================================================
  // Hard: Scoring function
  // =========================================================================

  private selectHard(engine: PachisiEngine, player: PlayerColor, validMoves: PachisiMove[]): PachisiMove {
    let bestScore = -Infinity;
    let bestMoves: PachisiMove[] = [];

    for (const move of validMoves) {
      const score = this.scoreMove(engine, player, move);
      if (score > bestScore) {
        bestScore = score;
        bestMoves = [move];
      } else if (score === bestScore) {
        bestMoves.push(move);
      }
    }

    // Break ties randomly
    return bestMoves[Math.floor(Math.random() * bestMoves.length)];
  }

  private scoreMove(engine: PachisiEngine, player: PlayerColor, move: PachisiMove): number {
    let score = 0;
    const pawns = engine.getPawnPositions(player);
    const pawn = pawns[move.pawnIndex];

    // Capture bonus (+50)
    if (this.wouldCapture(engine, player, move)) {
      score += 50;
    }

    // Reach home bonus (+100)
    if (this.wouldReachHome(engine, player, move)) {
      score += 100;
    }

    // Home column advance (+25)
    if (pawn.location === 'home_column') {
      score += 25;
    }

    // Safe square bonus (+20)
    if (this.wouldLandOnSafeSquare(engine, player, move)) {
      score += 20;
    }

    // Enter board bonus (+15)
    if (move.action === 'enter') {
      score += 15;
    }

    // Block bonus (+35): forming a block with own pawn
    if (this.wouldFormBlock(engine, player, move)) {
      score += 35;
    }

    // Blocking opponent's path bonus (+30)
    if (this.wouldBlockOpponent(engine, player, move)) {
      score += 30;
    }

    // Risk penalty (-20): landing within 6 of an opponent's pawn
    if (this.hasRisk(engine, player, move)) {
      score -= 20;
    }

    // Spread bonus (+10): move trailing pawn
    if (this.isTrailingPawn(engine, player, move)) {
      score += 10;
    }

    return score;
  }

  // =========================================================================
  // Move Analysis Helpers
  // =========================================================================

  private wouldCapture(engine: PachisiEngine, player: PlayerColor, move: PachisiMove): boolean {
    const destPos = this.getDestinationTrackPosition(engine, player, move);
    if (destPos === null) return false;
    if (SAFE_SQUARES.includes(destPos)) return false;

    const occupants = engine.getSquareOccupants(destPos);
    const opponents = occupants.filter((o) => o.player !== player);
    if (opponents.length === 0) return false;

    // Can't capture a block (2+ same-color pawns)
    const block = engine.hasBlock(destPos);
    if (block.blocked && block.player !== player) return false;

    return true;
  }

  private wouldLandOnSafeSquare(_engine: PachisiEngine, player: PlayerColor, move: PachisiMove): boolean {
    const destPos = this.getDestinationTrackPosition(_engine, player, move);
    if (destPos === null) return false;
    return SAFE_SQUARES.includes(destPos);
  }

  private wouldReachHome(engine: PachisiEngine, player: PlayerColor, move: PachisiMove): boolean {
    const pawns = engine.getPawnPositions(player);
    const pawn = pawns[move.pawnIndex];
    if (pawn.location === 'home_column') {
      return pawn.homeColumnPosition! + move.moveValue === HOME_COLUMN_LENGTH;
    }
    if (pawn.location === 'track') {
      const startPos = PLAYER_START_POSITIONS[player];
      const entryPos = HOME_COLUMN_ENTRY[player];
      const relativePos = (pawn.trackPosition! - startPos + TRACK_LENGTH) % TRACK_LENGTH;
      const relativeEntry = (entryPos - startPos + TRACK_LENGTH) % TRACK_LENGTH;
      const newRelativePos = relativePos + move.moveValue;
      if (relativePos <= relativeEntry && newRelativePos > relativeEntry) {
        const stepsInto = newRelativePos - relativeEntry - 1;
        return stepsInto === HOME_COLUMN_LENGTH;
      }
    }
    return false;
  }

  private wouldFormBlock(engine: PachisiEngine, player: PlayerColor, move: PachisiMove): boolean {
    const destPos = this.getDestinationTrackPosition(engine, player, move);
    if (destPos === null) return false;

    // Check if there's already one own pawn at destination
    const state = engine.getGameState();
    const ownAtDest = state.pawns[player].filter(
      (p) => p.location === 'track' && p.trackPosition === destPos && p.pawnIndex !== move.pawnIndex
    ).length;
    return ownAtDest === 1;
  }

  private wouldBlockOpponent(engine: PachisiEngine, player: PlayerColor, move: PachisiMove): boolean {
    const destPos = this.getDestinationTrackPosition(engine, player, move);
    if (destPos === null) return false;

    const state = engine.getGameState();
    for (const color of state.activePlayers) {
      if (color === player) continue;
      const entry = HOME_COLUMN_ENTRY[color];
      const dist = (entry - destPos + TRACK_LENGTH) % TRACK_LENGTH;
      if (dist > 0 && dist <= 10) {
        return true;
      }
    }
    return false;
  }

  private hasRisk(engine: PachisiEngine, player: PlayerColor, move: PachisiMove): boolean {
    const destPos = this.getDestinationTrackPosition(engine, player, move);
    if (destPos === null) return false;
    if (SAFE_SQUARES.includes(destPos)) return false;

    // If we'd form a block, no risk
    if (this.wouldFormBlock(engine, player, move)) return false;

    const state = engine.getGameState();
    for (const color of state.activePlayers) {
      if (color === player) continue;
      for (const pawn of state.pawns[color]) {
        if (pawn.location === 'track') {
          const dist = (destPos - pawn.trackPosition! + TRACK_LENGTH) % TRACK_LENGTH;
          if (dist > 0 && dist <= 25) { // Max cowrie value is 25
            return true;
          }
        }
      }
    }
    return false;
  }

  private isTrailingPawn(engine: PachisiEngine, player: PlayerColor, move: PachisiMove): boolean {
    const pawns = engine.getPawnPositions(player);
    const pawn = pawns[move.pawnIndex];
    if (pawn.location !== 'track') return false;

    const startPos = PLAYER_START_POSITIONS[player];
    const relPos = (pawn.trackPosition! - startPos + TRACK_LENGTH) % TRACK_LENGTH;

    let isTrailing = true;
    for (const p of pawns) {
      if (p === pawn) continue;
      if (p.location === 'track') {
        const otherRel = (p.trackPosition! - startPos + TRACK_LENGTH) % TRACK_LENGTH;
        if (otherRel < relPos) {
          isTrailing = false;
          break;
        }
      }
    }
    return isTrailing;
  }

  private selectClosestToHome(engine: PachisiEngine, player: PlayerColor, validMoves: PachisiMove[]): PachisiMove {
    const pawns = engine.getPawnPositions(player);
    const startPos = PLAYER_START_POSITIONS[player];
    let bestMove = validMoves[0];
    let bestProgress = -1;

    for (const move of validMoves) {
      const pawn = pawns[move.pawnIndex];
      let progress = 0;

      if (pawn.location === 'home_column') {
        progress = TRACK_LENGTH + pawn.homeColumnPosition!;
      } else if (pawn.location === 'track') {
        progress = (pawn.trackPosition! - startPos + TRACK_LENGTH) % TRACK_LENGTH;
      }

      if (progress > bestProgress) {
        bestProgress = progress;
        bestMove = move;
      }
    }

    return bestMove;
  }

  /** Get the destination track position for a move (null if not landing on track) */
  private getDestinationTrackPosition(engine: PachisiEngine, player: PlayerColor, move: PachisiMove): number | null {
    if (move.action === 'enter') {
      return PLAYER_START_POSITIONS[player];
    }

    const pawns = engine.getPawnPositions(player);
    const pawn = pawns[move.pawnIndex];

    if (pawn.location === 'track') {
      const startPos = PLAYER_START_POSITIONS[player];
      const entryPos = HOME_COLUMN_ENTRY[player];
      const relativePos = (pawn.trackPosition! - startPos + TRACK_LENGTH) % TRACK_LENGTH;
      const relativeEntry = (entryPos - startPos + TRACK_LENGTH) % TRACK_LENGTH;
      const newRelativePos = relativePos + move.moveValue;

      if (relativePos <= relativeEntry && newRelativePos > relativeEntry) {
        return null;
      }
      return (pawn.trackPosition! + move.moveValue) % TRACK_LENGTH;
    }

    return null;
  }
}
